/* eslint-disable no-fallthrough */
import { Dispatch, useReducer } from 'react';

import { ContentParametersMapping, ParameterRange, TreatmentTypeParameters } from 'data/generated/graphql';

interface TreatmentType {
  treatmentType: string;
  label: string;
}

export interface RegisterState {
  allessehIds: string[];
  treatmentTypes: TreatmentType[];
  mappings: ContentParametersMapping[];
  stepIndex: number;
  skippedNewLabelStep: boolean;
}

export type RegisterAction =
  | {
      type: 'SET_ALLESSEH_IDS';
      payload: {
        allessehIds: string[];
      };
    }
  | {
      type: 'SET_TREATMENT_TYPES';
      payload: {
        treatmentTypes: TreatmentType[];
      };
    }
  | {
      type: 'GENERATE_MAPPINGS';
      payload?: {
        defaultParametersByTreatment?: TreatmentTypeParameters[];
      };
    }
  | {
      type: 'SET_STEP_INDEX';
      payload: {
        index: number;
      };
    }
  | {
      type: 'UPDATE_PARAMETERS';
      payload: {
        treatmentType: string;
        value: ParameterRange[];
      };
    }
  | {
      type: 'SET_SKIPPED_NEW_LABEL_STEP';
      payload: {
        skipped: boolean;
      };
    };

function reducer(registerState: RegisterState, action: RegisterAction) {
  switch (action.type) {
    case 'SET_ALLESSEH_IDS': {
      return {
        ...registerState,
        allessehIds: action.payload.allessehIds
      };
    }
    case 'SET_TREATMENT_TYPES': {
      return {
        ...registerState,
        treatmentTypes: action.payload.treatmentTypes
      };
    }
    case 'GENERATE_MAPPINGS': {
      const defaultParametersByTreatment: TreatmentTypeParameters[] | undefined =
        action.payload?.defaultParametersByTreatment;

      return {
        ...registerState,
        // @ts-expect-error We don't need to provide all fields, as they will be set in the backend. Only treatmentType and parameters are needed.
        mappings: registerState.treatmentTypes.map<ContentParametersMapping>(({ treatmentType, label }) => {
          // Try to get existing parameters from state first
          const existingParams = registerState.mappings.find((m) => m.treatmentType === treatmentType)?.parameters;

          if (existingParams && existingParams.length > 0) {
            return { treatmentType, label, parameters: existingParams };
          }

          // Otherwise, use defaults from CONFIG if available
          const defaultValue = defaultParametersByTreatment?.find((d) => d.treatmentType === treatmentType);

          if (defaultValue) {
            return { treatmentType, label, parameters: defaultValue.parameters };
          }

          // Fall back to empty array if no defaults available
          return { treatmentType, label, parameters: [] };
        })
      };
    }
    case 'SET_STEP_INDEX': {
      return {
        ...registerState,
        stepIndex: action.payload.index
      };
    }
    case 'UPDATE_PARAMETERS': {
      const modifiedMapping = registerState.mappings.find(
        (mapping) => mapping.treatmentType === (action.payload as { treatmentType: string }).treatmentType
      );

      if (modifiedMapping) {
        const updatedMapping = { ...modifiedMapping, parameters: action.payload.value };
        const updatedMappings = registerState.mappings.map((m) =>
          m.treatmentType === action.payload.treatmentType ? updatedMapping : m
        );
        return { ...registerState, mappings: updatedMappings };
      }

      throw new Error(
        `Mapping with treatmentType ${action.payload.treatmentType} not found when attempting to update parameters.`
      );
    }
    case 'SET_SKIPPED_NEW_LABEL_STEP': {
      return {
        ...registerState,
        skippedNewLabelStep: action.payload.skipped
      };
    }
    default:
      return registerState;
  }
}

export type RegisterActionDispatchFn = Dispatch<RegisterAction>;

export function useRegisterState() {
  const [registerState, dispatch] = useReducer(reducer, {
    allessehIds: [],
    treatmentTypes: [],
    mappings: [],
    stepIndex: 1,
    skippedNewLabelStep: false
  });

  return { registerState, dispatch };
}
